/**
 * QiblaScene.jsx
 * --------------
 * The visual heart of the card: the Kaaba window and the prayer rug,
 * layered together in a single relative container so we can:
 *   • bring both images as close together as possible (no wasteful gap),
 *   • let the rug image render slightly wider than the card so its
 *     visible height grows without changing its aspect ratio,
 *   • float the BubbleLevel at the geometric center of the scene —
 *     which lands near the middle of a typical mobile screen.
 *
 * Purely presentational.
 */
import React from 'react';
import { BubbleLevel } from './BubbleLevel';

const RUG_URL   = '/assets/qibla/prayer_rug.png';
const KAABA_URL = '/assets/qibla/kaaba_window.png';

/* Scene size tokens — kept as constants so the BubbleLevel offset is
   easy to reason about relative to the Kaaba/Rug positions. */
const SCENE_HEIGHT     = 520;   // total scene height in px
const KAABA_SLOT_HEIGHT = 260;  // top area allocated to the Kaaba image
const KAABA_MAX_WIDTH   = 320;  // Kaaba image can grow up to this width
const RUG_WIDTH_PCT     = 118;  // rug bleeds slightly wider than the card

export const QiblaScene = ({
  kaabaOpacity,
  aligned,
  tiltLevel,
  bubbleOffsetX,
  bubbleOffsetY,
  bubbleHint,
}) => (
  <div
    className="w-full relative overflow-hidden"
    style={{ height: SCENE_HEIGHT }}
  >
    {/* Rug layer — bottom of scene. Rendered slightly wider than the
        card so its visible height grows (rug aspect ratio ≈ 1.83:1;
        wider ⇒ taller in absolute pixels).  Rendered FIRST so the
        Kaaba window naturally stacks ON TOP of the rug in the overlap
        area near the middle of the scene. */}
    <div
      className="absolute bottom-0 left-1/2 -translate-x-1/2 pointer-events-none"
      style={{ width: `${RUG_WIDTH_PCT}%` }}
      data-testid="qibla-prayer-rug"
    >
      <img
        src={RUG_URL}
        alt=""
        className="block w-full object-contain"
        draggable={false}
      />
    </div>

    {/* Kaaba layer — top of scene. Its slot is bottom-aligned so the
        Kaaba image sits at the LOWER edge of its slot, right above the
        rug's top edge (minimising the vertical gap). z-index keeps it
        painted on top of the rug in the overlap area. */}
    <div
      className="absolute left-1/2 -translate-x-1/2 pointer-events-none flex items-end justify-center"
      style={{
        top: 0,
        height: KAABA_SLOT_HEIGHT,
        width: '95%',
        maxWidth: KAABA_MAX_WIDTH,
        opacity: kaabaOpacity,
        transition: 'opacity 260ms ease-out',
        filter: aligned
          ? 'drop-shadow(0 0 22px rgba(212,175,55,0.55))'
          : 'none',
        zIndex: 2,
      }}
      data-testid="qibla-kaaba-window"
    >
      <img
        src={KAABA_URL}
        alt=""
        className="max-w-full max-h-full object-contain"
        draggable={false}
      />
    </div>

    {/* Bubble — geometric center of the scene ⇒ visually near the
        centre of a typical mobile screen. Uses `top: 50%` with a
        negative Y translate so its centre point sits exactly on the
        midline. */}
    <div
      className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2"
      style={{ top: '50%', zIndex: 5 }}
    >
      <BubbleLevel
        level={tiltLevel}
        offsetX={bubbleOffsetX}
        offsetY={bubbleOffsetY}
        hint={bubbleHint}
      />
    </div>
  </div>
);
